export const OFFERWALL_ACCESS_KEY = 'haber:offerwall:accessUntil'
export const OFFERWALL_ACCESS_EVENT = 'haber:offerwall-access-changed'

export type ArticleAccessMode = 'open' | 'members' | 'rewarded'
export type OfferwallProvider = 'gam_rewarded' | 'adsense'
export type OfferwallPolicy = {
  mode: ArticleAccessMode
  provider: OfferwallProvider
  enabled: boolean
  path: string
  minutes: number
}

export function resolveOfferwallArticleDecision(
  policy: OfferwallPolicy,
  context: {
    isMember: boolean
    hasRewardAccess: boolean
    alreadyRead: boolean
    unavailable: boolean
    nativeAttempt: boolean
    nativeDismissed: boolean
  },
): 'open' | 'members' | 'rewarded' | 'support' {
  if (policy.mode === 'members') return context.isMember ? 'open' : 'members'
  if (policy.mode === 'open' || !policy.enabled) return 'open'
  if (policy.provider === 'adsense') {
    return context.hasRewardAccess || context.nativeAttempt || context.nativeDismissed ? 'open' : 'support'
  }
  return !policy.path || context.hasRewardAccess || context.alreadyRead || context.unavailable
    ? 'open'
    : 'rewarded'
}

/** null means storage is unavailable; the soft gate must stay open in that case. */
export function readOfferwallAccessUntil(): number | null {
  if (typeof window === 'undefined') return 0
  try {
    const until = Number(window.localStorage.getItem(OFFERWALL_ACCESS_KEY))
    return Number.isFinite(until) && until > Date.now() ? until : 0
  } catch {
    return null
  }
}

export function hasOfferwallAccess(): boolean {
  const until = readOfferwallAccessUntil()
  return until === null || until > Date.now()
}

/** Preloaded articles are not read yet; an expired grant must not keep those open. */
export function rememberOfferwallArticleAccess(alreadyRead: boolean, isVisible: boolean): boolean {
  return alreadyRead || (isVisible && hasOfferwallAccess())
}

export function shouldResetOfferwallAttempt(
  previous: { enabled: boolean; path: string; minutes: number; mode?: ArticleAccessMode; provider?: OfferwallProvider } | null,
  next: { enabled: boolean; path: string; minutes: number; mode?: ArticleAccessMode; provider?: OfferwallProvider },
): boolean {
  return previous !== null && (previous.enabled !== next.enabled || previous.path !== next.path
    || previous.mode !== next.mode || previous.provider !== next.provider)
}

/** Called only after GPT confirms the reward, never for an unavailable ad. */
export function grantOfferwallAccess(minutes: number): number {
  const duration = Number.isFinite(minutes) ? Math.min(1440, Math.max(1, minutes)) : 30
  const until = Date.now() + duration * 60_000
  try {
    window.localStorage.setItem(OFFERWALL_ACCESS_KEY, String(until))
  } catch {
    // Storage restrictions already make the soft gate fail open.
  }
  window.dispatchEvent(new Event(OFFERWALL_ACCESS_EVENT))
  return until
}

/** localStorage does not emit storage events in the tab that wrote the value. */
export function subscribeOfferwallAccess(onChange: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === OFFERWALL_ACCESS_KEY || event.key === null) onChange()
  }
  window.addEventListener(OFFERWALL_ACCESS_EVENT, onChange)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(OFFERWALL_ACCESS_EVENT, onChange)
    window.removeEventListener('storage', onStorage)
  }
}
